import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight, Quote, User } from 'lucide-react';
import { testimonials } from '../data/mock';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const minSwipeDistance = 50;

  const nextTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, []);

  const prevTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 6000);
    return () => clearInterval(interval);
  }, [nextTestimonial]);

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) nextTestimonial();
    else if (isRightSwipe) prevTestimonial();
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  return (
    <section className="testimonials-section" ref={ref}>
      <div className="testimonials-container">
        {/* Section Header */}
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.span 
            className="section-tag"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            Testimonials
          </motion.span>
          <motion.h2 
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3 }}
            style={{ opacity: isInView ? 1 : 0 }}
          >
            What Our Clients
            <span className="gradient-text"> Say</span>
          </motion.h2>
        </motion.div>

        {/* Testimonials Carousel */}
        <motion.div 
          className="testimonials-carousel"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
        >
          <motion.button 
            className="carousel-btn prev" 
            onClick={prevTestimonial} 
            aria-label="Previous testimonial"
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
            whileTap={{ scale: 0.95 }}
            data-testid="prev-testimonial-btn"
          >
            <ChevronLeft size={24} />
          </motion.button>

          <div 
            className="testimonial-cards"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <AnimatePresence mode="wait" custom={currentIndex}>
              <motion.div
                key={currentIndex}
                className="testimonial-card active"
                custom={currentIndex}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: 'spring', stiffness: 200, damping: 25 }}
                style={{ position: 'relative', width: '100%', height: '100%' }}
              >
                <div className="testimonial-content">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 0.5, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Quote className="quote-icon" size={36} />
                  </motion.div>
                  
                  <motion.p 
                    className="testimonial-text"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    style={{ opacity: 1 }}
                  >
                    {testimonials[currentIndex].content}
                  </motion.p>
                  
                  <motion.div 
                    className="testimonial-rating"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    style={{ opacity: 1 }}
                  >
                    {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4 + i * 0.05 }}
                      >
                        <Star size={18} fill="#3b82f6" color="#3b82f6" />
                      </motion.div>
                    ))}
                  </motion.div>
                  
                  <motion.div 
                    className="testimonial-author"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    style={{ opacity: 1 }}
                  >
                    <div className="author-avatar">
                      <User size={24} strokeWidth={1.5} />
                    </div>
                    <div className="author-info">
                      <h4>{testimonials[currentIndex].name}</h4>
                      <p>{testimonials[currentIndex].position}</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <motion.button 
            className="carousel-btn next" 
            onClick={nextTestimonial} 
            aria-label="Next testimonial"
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
            whileTap={{ scale: 0.95 }}
            data-testid="next-testimonial-btn"
          >
            <ChevronRight size={24} />
          </motion.button>
        </motion.div>

        {/* Dots Indicator */}
        <div className="carousel-dots">
          {testimonials.map((_, index) => (
            <motion.button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to testimonial ${index + 1}`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              animate={{
                scale: index === currentIndex ? 1.2 : 1,
                backgroundColor: index === currentIndex ? '#3b82f6' : 'rgba(59, 130, 246, 0.2)'
              }}
              data-testid={`testimonial-dot-${index}`}
            />
          ))}
        </div>
        
        <p className="swipe-hint">Swipe to see more</p>
      </div>
    </section>
  );
};

export default Testimonials;
